import React from 'react';
import { motion } from 'framer-motion';
import {
  Button,
  Card,
  EmptyState,
  Flex,
  Heading,
  Select,
  Switch,
  TagBadge,
  Text,
  TextField,
  UnderlineTab,
  UnderlineTabs,
  useToast,
} from '@chatsdet/ui';
import {
  BracketsCurly,
  DownloadSimple,
  Lightbulb,
  ListChecks,
  Plus,
  Trash,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import type {
  ActionInputType,
  WorkflowCredentialInputDefinition,
  WorkflowInputDefinition,
  WorkflowScenario,
  WorkflowValueInputDefinition,
} from '@chatsdet/types';
import { validateWorkflowInputExpressions } from '@chatsdet/types';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';
import { MacroRichTextField } from '../../../components/node-config/MacroRichTextField';
import { ScenarioListPanel } from './ScenarioListPanel';
import { parseScenarioImportPayload } from './scenarioImport';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  inputs: WorkflowInputDefinition[];
  scenarios: WorkflowScenario[];
  credentialTemplates: CredentialTemplate[];
  onSaveInputs: (inputs: WorkflowInputDefinition[]) => void;
  onSaveScenarios: (scenarios: WorkflowScenario[]) => void;
  isSavingInputs?: boolean;
  isSavingScenarios?: boolean;
  readOnly?: boolean;
};

const INPUT_TYPE_OPTIONS = [
  { id: 'text', label: 'Text' },
  { id: 'email', label: 'Email' },
  { id: 'number', label: 'Number' },
  { id: 'password', label: 'Password' },
  { id: 'textarea', label: 'Textarea' },
  { id: 'credential', label: 'Persona' },
];

function slugifyName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function humanizeName(value: string) {
  return value
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeEditableInputs(inputs: WorkflowInputDefinition[]) {
  return inputs.map((input) => {
    const name = typeof input.name === 'string' ? input.name : '';
    const label = typeof input.label === 'string' ? input.label : '';
    return {
      ...input,
      name,
      label: label || humanizeName(name),
    } as WorkflowInputDefinition;
  });
}

function makeValueInput(): WorkflowValueInputDefinition {
  return { kind: 'value', name: '', label: '', type: 'text' };
}

function makeCredentialInput(): WorkflowCredentialInputDefinition {
  return {
    kind: 'credential',
    name: '',
    label: '',
    credentialFields: { username: true, password: true },
  };
}

function buildSchemaDownload(inputs: WorkflowInputDefinition[]) {
  return {
    schemaVersion: 1,
    inputs,
    example: {
      scenarios: [
        {
          name: 'Happy path',
          values: Object.fromEntries(
            inputs.filter((i) => i.kind === 'value').map((i) => [i.name, ''])
          ),
          credentials: Object.fromEntries(
            inputs
              .filter((i) => i.kind === 'credential')
              .map((i) => [i.name, { credentialTemplateId: i.defaultCredentialTemplateId ?? '' }])
          ),
        },
      ],
    },
  };
}

export function ScenarioConfigOverlay({
  isOpen,
  onClose,
  inputs,
  scenarios,
  credentialTemplates,
  onSaveInputs,
  onSaveScenarios,
  isSavingInputs,
  isSavingScenarios,
  readOnly,
}: Props) {
  const [tab, setTab] = React.useState<'schema' | 'scenarios'>('schema');
  const [draftInputs, setDraftInputs] = React.useState<WorkflowInputDefinition[]>(() =>
    normalizeEditableInputs(inputs)
  );
  const fileRef = React.useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();

  // Seed the editable draft from the saved inputs only when the overlay opens
  // (false → true), not on every `inputs` change while it is open. The `inputs`
  // prop comes from a React Query result that refetches on window focus, so
  // re-seeding on each change would silently discard the user's in-progress
  // edits when they tab away and back. saveSchema() keeps the draft in sync
  // after an explicit save.
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (isOpen && !wasOpen.current) setDraftInputs(normalizeEditableInputs(inputs));
    wasOpen.current = isOpen;
  }, [isOpen, inputs]);

  const templateItems = credentialTemplates.map((template) => ({
    id: template.id,
    label: template.name,
  }));

  const hasUnsavedChanges = React.useMemo(
    () => JSON.stringify(draftInputs) !== JSON.stringify(inputs),
    [draftInputs, inputs]
  );
  const inputExpressionIssues = React.useMemo(
    () => validateWorkflowInputExpressions(draftInputs),
    [draftInputs]
  );

  const updateInput = (index: number, patch: Partial<WorkflowInputDefinition>) => {
    setDraftInputs((prev) =>
      prev.map((input, i) => {
        if (i !== index) return input;
        if (patch.kind === 'credential' && input.kind !== 'credential') {
          return { ...makeCredentialInput(), name: input.name, label: input.label };
        }
        if (patch.kind === 'value' && input.kind !== 'value') {
          return { ...makeValueInput(), name: input.name, label: input.label };
        }
        return { ...input, ...patch } as WorkflowInputDefinition;
      })
    );
  };

  const addInput = () => setDraftInputs((prev) => [...prev, makeValueInput()]);
  const removeInput = (index: number) =>
    setDraftInputs((prev) => prev.filter((_, i) => i !== index));

  const saveSchema = () => {
    const normalized = draftInputs
      .map((input) => {
        const currentName = typeof input.name === 'string' ? input.name.trim() : '';
        const currentLabel = typeof input.label === 'string' ? input.label.trim() : '';
        const label = currentLabel || humanizeName(currentName);
        return {
          ...input,
          name: currentName || slugifyName(label),
          label,
        } as WorkflowInputDefinition;
      })
      .filter((input) => input.name && input.label);
    const normalizedIssues = validateWorkflowInputExpressions(normalized);
    if (normalizedIssues.length > 0) {
      toast.error(normalizedIssues[0]!.message.replaceAll('`', ''), { reportError: false });
      return;
    }
    // Keep the local draft consistent with what we just saved. The draft no
    // longer re-seeds from the `inputs` prop after a save-triggered refetch
    // (see the open-transition effect above), so settle it here instead so the
    // "All changes saved" indicator reflects the persisted state.
    setDraftInputs(normalized);
    onSaveInputs(normalized);
  };

  const downloadSchema = () => {
    const blob = new Blob([JSON.stringify(buildSchemaDownload(inputs), null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'workflow-scenario-schema.json';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const uploadScenarios = async (file: File | undefined) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as unknown;
      const imported = parseScenarioImportPayload(parsed);
      if (!imported) throw new Error('Missing scenarios array');
      onSaveScenarios([...scenarios, ...imported]);
      toast.success(`Imported ${imported.length} scenario${imported.length === 1 ? '' : 's'}`);
    } catch {
      toast.error('Could not import scenarios. Check the JSON shape and try again.', {
        reportError: false,
      });
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const renderInput = (input: WorkflowInputDefinition, index: number) => {
    const type = input.kind === 'credential' ? 'credential' : (input.type ?? 'text');
    const typeLabel = INPUT_TYPE_OPTIONS.find((opt) => opt.id === type)?.label ?? 'Text';
    // Use the exact saved variable name so the helper matches runtime lookup.
    const resolvedName = input.name.trim();
    const referenceToken = `$var.${resolvedName || 'name'}`;
    const expressionIssue = inputExpressionIssues.find((issue) => issue.inputIndex === index);
    const suggestionVariables = draftInputs
      .slice(0, index)
      .filter(
        (previous): previous is WorkflowValueInputDefinition =>
          previous.kind === 'value' && previous.name.trim().length > 0
      )
      .map((previous) => ({
        kind: 'variable' as const,
        name: previous.name.trim(),
        source: 'workflowInput' as const,
        description: 'Earlier workflow input',
        designTimeValue: previous.defaultValue,
      }));
    return (
      <motion.div
        key={`${input.kind}-${index}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18, delay: Math.min(index * 0.025, 0.2) }}
      >
        <Card
          padding="none"
          className="group gap-y-0 overflow-hidden bg-muted/30 shadow-sm transition-shadow hover:shadow-md"
        >
          <Flex
            align="center"
            justify="between"
            className="border-b border-border/60 bg-muted/50 px-4 py-2"
          >
            <Flex align="center" gap="sm">
              <Text
                size="xs"
                weight="semibold"
                className="uppercase tracking-wider text-muted-foreground"
              >
                Input {index + 1}
              </Text>
              <TagBadge
                name={typeLabel}
                color={input.kind === 'credential' ? 'purple' : 'blue'}
                size="sm"
                hideDot
              />
            </Flex>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Remove input"
              onPress={() => removeInput(index)}
              isDisabled={readOnly}
              iconLeft={<Trash size={14} />}
              className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
            />
          </Flex>

          <div className="p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px]">
              <TextField
                label="Label"
                description="Human-friendly name shown when running the workflow."
                value={input.label}
                placeholder="User Email"
                onChange={(label) =>
                  updateInput(index, {
                    label,
                    name: input.name || slugifyName(label),
                  } as Partial<WorkflowInputDefinition>)
                }
              />
              <div>
                <Select
                  label="Type"
                  items={INPUT_TYPE_OPTIONS}
                  selectedKey={type}
                  onSelectionChange={(key) => {
                    if (key === 'credential') updateInput(index, { kind: 'credential' });
                    else updateInput(index, { kind: 'value', type: key as ActionInputType });
                  }}
                />
                <Text className="mt-1 text-xs text-muted-foreground">
                  How the value is entered and validated.
                </Text>
              </div>
              <TextField
                label="Variable name"
                description={
                  <>
                    Reference it in steps as{' '}
                    <span className="font-mono text-foreground/80">{referenceToken}</span>
                  </>
                }
                value={input.name}
                placeholder="user_email"
                onChange={(name) =>
                  updateInput(index, { name } as Partial<WorkflowInputDefinition>)
                }
              />
              <div className="flex items-end justify-end pb-1">
                <Switch
                  size="sm"
                  label="Required"
                  isSelected={!!input.required}
                  onChange={(required) =>
                    updateInput(index, {
                      required: required || undefined,
                    } as Partial<WorkflowInputDefinition>)
                  }
                />
              </div>
            </div>

            <div className="mt-3">
              {input.kind === 'credential' ? (
                <div>
                  <Select
                    label="Default persona"
                    items={templateItems}
                    placeholder="No default"
                    selectedKey={input.defaultCredentialTemplateId ?? null}
                    onSelectionChange={(key) =>
                      updateInput(index, {
                        defaultCredentialTemplateId: key as string,
                      } as Partial<WorkflowInputDefinition>)
                    }
                  />
                  <Text className="mt-1 text-xs text-muted-foreground">
                    Persona used when a scenario doesn't pick one.
                  </Text>
                </div>
              ) : input.type === 'password' ? (
                <TextField
                  label="Default value"
                  description="Used when a scenario doesn't set this input."
                  value={input.defaultValue ?? ''}
                  type="password"
                  errorMessage={expressionIssue?.message.replaceAll('`', '')}
                  onChange={(defaultValue) =>
                    updateInput(index, { defaultValue } as Partial<WorkflowInputDefinition>)
                  }
                />
              ) : (
                <MacroRichTextField
                  label="Default value"
                  aria-label="Default value"
                  description="Used when a scenario doesn't set this input. Type $ for dynamic values."
                  value={input.defaultValue ?? ''}
                  placeholder="Optional — type $ for macros or earlier inputs"
                  multiline={input.type === 'textarea'}
                  rows={3}
                  includeVariables
                  suggestionVariables={suggestionVariables}
                  errorMessage={expressionIssue?.message.replaceAll('`', '')}
                  onChange={(defaultValue) =>
                    updateInput(index, { defaultValue } as Partial<WorkflowInputDefinition>)
                  }
                />
              )}
            </div>
          </div>
        </Card>
      </motion.div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: [0.32, 0.72, 0, 1] } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: [0.32, 0.72, 0, 1] } }}
        className="absolute inset-0 z-40 bg-background"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-4 left-4 z-50"
      >
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Close scenarios"
          onPress={onClose}
          className="h-9 p-0"
          aria-label="Close scenarios"
          iconLeft={<X size={20} weight="bold" />}
        />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col items-center"
      >
        <div className="w-full max-w-4xl px-6 pt-6 pb-4 shrink-0">
          <Flex align="center" justify="between" gap="md" className="mb-5">
            <div className="min-w-0">
              <Heading level={4} className="mb-1 tracking-tight">
                Workflow Scenarios
              </Heading>
              <Text size="sm" className="text-muted-foreground">
                Define workflow inputs and reusable scenario data for runs.
              </Text>
            </div>
          </Flex>

          <Flex align="center" justify="between" gap="sm" className="border-b border-border">
            <UnderlineTabs
              selectedId={tab}
              onSelectionChange={(id) => setTab(id as 'schema' | 'scenarios')}
              className="border-b-0"
            >
              <UnderlineTab id="schema">
                <Flex align="center" gap="xs">
                  <BracketsCurly size={14} />
                  Schema
                </Flex>
              </UnderlineTab>
              <UnderlineTab id="scenarios">
                <Flex align="center" gap="xs">
                  <ListChecks size={14} />
                  Scenarios
                </Flex>
              </UnderlineTab>
            </UnderlineTabs>
            <Flex align="center" gap="xs">
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Download schema JSON"
                onPress={downloadSchema}
                iconLeft={<DownloadSimple size={16} />}
              />
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Import scenarios JSON"
                onPress={() => fileRef.current?.click()}
                iconLeft={<UploadSimple size={16} />}
              />
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(event) => void uploadScenarios(event.target.files?.[0])}
              />
            </Flex>
          </Flex>

          {tab === 'schema' && (
            <Flex align="center" justify="end" gap="sm" className="mt-3">
              <Text
                size="xs"
                className={hasUnsavedChanges ? 'text-muted-foreground' : 'text-muted-foreground/70'}
              >
                {hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
              </Text>
              <Button
                variant="primary"
                size="sm"
                onPress={saveSchema}
                isDisabled={
                  isSavingInputs ||
                  readOnly ||
                  !hasUnsavedChanges ||
                  inputExpressionIssues.length > 0
                }
                iconLeft={<BracketsCurly size={14} />}
              >
                {isSavingInputs ? 'Saving...' : 'Save schema'}
              </Button>
            </Flex>
          )}
        </div>

        <div className="w-full max-w-4xl flex-1 min-h-0 px-6 pb-6">
          <div className="h-full overflow-y-auto">
            {tab === 'schema' ? (
              <div className="space-y-4">
                <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-3.5 py-2.5">
                  <Lightbulb size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <Text size="xs" className="leading-relaxed text-muted-foreground">
                    <span className="font-medium text-foreground">Inputs</span> are the variables
                    your workflow accepts — each becomes a{' '}
                    <span className="font-mono text-foreground/80">$var.name</span> token you can
                    use in any step. <span className="font-medium text-foreground">Scenarios</span>{' '}
                    (next tab) are named sets of values for those inputs. New here? Add an input
                    below, then save a scenario for it.
                  </Text>
                </div>
                {draftInputs.length > 0 && (
                  <div>
                    <Heading level={5} className="font-semibold tracking-tight">
                      Inputs
                    </Heading>
                    <Text size="sm" className="text-muted-foreground">
                      {draftInputs.length} input{draftInputs.length === 1 ? '' : 's'} available to
                      scenarios
                    </Text>
                  </div>
                )}
                <div className="space-y-3">
                  {draftInputs.map(renderInput)}
                  {draftInputs.length > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={addInput}
                      isDisabled={readOnly}
                      iconLeft={<Plus size={14} />}
                      className="w-full border-dashed"
                    >
                      Add input
                    </Button>
                  ) : (
                    <EmptyState
                      icon={<BracketsCurly size={22} />}
                      title="No inputs yet"
                      description="Add inputs to make this workflow parameterized."
                      action={
                        <Button
                          variant="primary"
                          size="sm"
                          onPress={addInput}
                          isDisabled={readOnly}
                          iconLeft={<Plus size={14} />}
                        >
                          Add input
                        </Button>
                      }
                    />
                  )}
                </div>
              </div>
            ) : (
              <ScenarioListPanel
                inputs={inputs}
                scenarios={scenarios}
                onSave={onSaveScenarios}
                isSaving={isSavingScenarios}
                credentialTemplates={credentialTemplates}
              />
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
